<template>
  <div class="w-full p-4">
    <!-- The band travels with the page, as on the backlog board: the search
         and the counts are what you reach for after scrolling, and -mt-4
         cancels the page's own padding so it starts where it pins instead of
         sliding 16px first. -->
    <div class="sticky top-14 z-30 bg-gray-200 -mx-4 -mt-4 px-4 pt-3 pb-4">
      <div class="flex flex-row justify-between items-center pb-2 border-b border-b-gray-400 mb-3">
        <div class="flex items-center gap-1.5">
          <h2 class="text-lg font-semibold">Sprints</h2>
          <span class="relative group">
            <information-circle-icon
              class="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help"
            />
            <span
              class="hidden group-hover:block absolute left-0 top-5 z-30 w-80 rounded-md
                     border border-gray-400 bg-white p-2 text-xs text-gray-600 shadow-lg"
            >
              The sprint calendar the whole Back Office reads. Latest first.
              Year and quarter are worked out from the start date and cannot be
              typed. A sprint holding work items cannot be deleted — move its
              items off first. Renaming a week renames it everywhere it
              appears, and moving its dates moves which sprint the backlog
              board calls the current one.
            </span>
          </span>
        </div>
        <button
          v-if="canEdit"
          class="px-3 py-1.5 text-sm rounded-md bg-accent-primary text-white
                 hover:bg-accent-secondary"
          @click="openNew"
        >+ New sprint</button>
      </div>

      <div class="flex flex-wrap items-end gap-3">
        <label class="block">
          <span class="block text-xs text-gray-500 mb-0.5">Search</span>
          <input
            v-model="search"
            type="text"
            placeholder="Sprint name"
            class="h-9 px-2 border border-gray-400 rounded-md"
          />
        </label>
        <label class="block">
          <span class="block text-xs text-gray-500 mb-0.5">Year</span>
          <select v-model="year" class="h-9 px-2 border border-gray-400 rounded-md">
            <option value="">All years</option>
            <option v-for="y in years" :key="y" :value="String(y)">{{ y }}</option>
            <!-- The sixteen rows the Airtable import left undated are whole
                 buckets rather than weeks, and they have no year to file
                 them under, so they get their own choice rather than
                 disappearing the moment a year is picked. -->
            <option v-if="undatedCount" value="undated">
              No dates ({{ undatedCount }})
            </option>
          </select>
        </label>
        <label class="flex items-center gap-1.5 h-9 text-sm text-gray-600">
          <input v-model="emptyOnly" type="checkbox" />
          <!-- The only ones that can be deleted, which is the question
               somebody tidying the calendar is actually asking. -->
          Empty only
        </label>
        <span class="text-sm text-gray-600">
          {{ shown.length }} of {{ sprints.length }}
        </span>
      </div>

      <p v-if="error" class="mt-2 text-sm text-red-700">{{ error }}</p>
    </div>

    <p v-if="loading" class="text-gray-500 py-6 text-center">Loading…</p>

    <template v-else>
      <!-- Mobile card list -->
      <div class="md:hidden space-y-2">
        <p v-if="!shown.length" class="p-4 text-center text-gray-500 text-sm">
          No sprints match.
        </p>
        <div
          v-for="sprint in shown"
          :key="`m-${sprint.id}`"
          class="border rounded-md bg-white p-3"
          :class="sprint.id === currentId ? 'border-accent-primary' : 'border-gray-300'"
        >
          <div class="flex items-start justify-between gap-2">
            <div class="min-w-0">
              <div class="text-sm font-semibold break-words">{{ sprint.name }}</div>
              <div class="text-xs text-gray-500">{{ rangeLabel(sprint) }}</div>
            </div>
            <span class="flex-shrink-0 text-xs text-gray-500">
              {{ sprint.quarter_label || '—' }}
            </span>
          </div>
          <div class="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
            <span v-if="sprint.id === currentId" :class="BADGE.current">current</span>
            <span v-if="!sprint.start_date" :class="BADGE.undated">no dates</span>
            <span v-if="overlaps[sprint.id]" :class="BADGE.overlap">
              overlaps {{ overlaps[sprint.id] }}
            </span>
            <span class="text-gray-500">
              {{ sprint.work_item_count }}
              item{{ sprint.work_item_count === 1 ? '' : 's' }}
            </span>
          </div>
          <div v-if="canEdit" class="mt-3 flex flex-wrap gap-2">
            <button
              class="text-xs px-2 py-1 border border-gray-400 rounded hover:bg-gray-100"
              @click="openEdit(sprint)"
            >Edit</button>
            <button
              class="text-xs px-2 py-1 border rounded disabled:opacity-40"
              :class="deletable(sprint)
                ? 'border-red-500 text-red-700 hover:bg-red-50'
                : 'border-gray-300 text-gray-400'"
              :disabled="!deletable(sprint) || deleting === sprint.id"
              :title="deleteTitle(sprint)"
              @click="remove(sprint)"
            >{{ deleting === sprint.id ? 'Deleting…' : 'Delete' }}</button>
          </div>
        </div>
      </div>

      <!-- Desktop / tablet table -->
      <div class="hidden md:block border border-gray-400 rounded-md overflow-hidden">
        <table class="table-auto w-full text-sm">
          <thead class="bg-dark-primary text-white">
            <tr>
              <th class="text-left px-3 py-2">Name</th>
              <th class="text-left px-3 py-2">Start</th>
              <th class="text-left px-3 py-2">End</th>
              <th class="text-right px-3 py-2">Days</th>
              <th class="text-left px-3 py-2">Quarter</th>
              <th class="text-right px-3 py-2">Items</th>
              <th v-if="canEdit" class="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="sprint in shown"
              :key="sprint.id"
              class="border-t border-gray-200"
              :class="sprint.id === currentId ? 'bg-accent-primary/10' : ''"
            >
              <td class="px-3 py-2">
                <div class="flex flex-wrap items-center gap-1.5">
                  <span class="font-medium">{{ sprint.name }}</span>
                  <span v-if="sprint.id === currentId" :class="BADGE.current">current</span>
                  <span v-if="!sprint.start_date" :class="BADGE.undated">no dates</span>
                  <span
                    v-if="overlaps[sprint.id]"
                    :class="BADGE.overlap"
                    :title="`These two weeks cover the same days, so which one the board
                             calls current depends on which starts first`"
                  >overlaps {{ overlaps[sprint.id] }}</span>
                </div>
              </td>
              <td class="px-3 py-2 tabular-nums whitespace-nowrap">
                {{ dayLabel(sprint.start_date) }}
              </td>
              <td class="px-3 py-2 tabular-nums whitespace-nowrap">
                {{ dayLabel(sprint.end_date) }}
              </td>
              <td class="px-3 py-2 text-right tabular-nums">{{ dayCount(sprint) }}</td>
              <td class="px-3 py-2 text-gray-600">{{ sprint.quarter_label || '—' }}</td>
              <td
                class="px-3 py-2 text-right tabular-nums"
                :class="sprint.work_item_count ? '' : 'text-gray-400'"
              >{{ sprint.work_item_count }}</td>
              <td v-if="canEdit" class="px-3 py-2 text-right whitespace-nowrap">
                <button
                  class="text-xs px-2 py-1 border border-gray-400 rounded hover:bg-gray-100"
                  @click="openEdit(sprint)"
                >Edit</button>
                <button
                  class="ml-2 text-xs px-2 py-1 border rounded disabled:opacity-40"
                  :class="deletable(sprint)
                    ? 'border-red-500 text-red-700 hover:bg-red-50'
                    : 'border-gray-300 text-gray-400'"
                  :disabled="!deletable(sprint) || deleting === sprint.id"
                  :title="deleteTitle(sprint)"
                  @click="remove(sprint)"
                >{{ deleting === sprint.id ? 'Deleting…' : 'Delete' }}</button>
              </td>
            </tr>
            <tr v-if="!shown.length">
              <td :colspan="canEdit ? 7 : 6" class="px-3 py-4 text-center text-gray-500">
                No sprints match.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <!-- A small panel of its own rather than the shared BaseModal, which is
         three quarters of the window: this form is three fields. -->
    <div
      v-if="formOpen"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      @click.self="closeForm"
    >
      <div v-on-escape="closeForm" class="bg-white rounded-md shadow-lg p-5 w-[30rem]">
        <h3 class="text-base font-semibold mb-3">
          {{ editing ? 'Edit sprint' : 'New sprint' }}
        </h3>

        <label class="block text-sm mb-1">Start date</label>
        <input
          v-model="form.start_date"
          type="date"
          class="w-full p-1.5 border border-gray-400 rounded-md mb-1"
          @change="startChanged"
        />
        <p class="text-xs text-gray-500 mb-3">
          Picking a start date fills in the Friday of that week and names the
          sprint the way the other {{ sprints.length }} are named. Both can be
          typed over.
        </p>

        <label class="block text-sm mb-1">End date</label>
        <input
          v-model="form.end_date"
          type="date"
          class="w-full p-1.5 border border-gray-400 rounded-md mb-3"
          @change="endChanged"
        />

        <label class="block text-sm mb-1">Name</label>
        <input
          v-model="form.name"
          type="text"
          placeholder="2026: 11/09 - 11/13"
          class="w-full p-1.5 border border-gray-400 rounded-md mb-1"
          @input="nameTyped = true"
        />
        <p class="text-xs text-gray-500 mb-3">
          What the board prints on the column heading.
        </p>

        <!-- Said rather than hidden: a sprint with items on it can be edited
             freely, and the point is that the edit is not local to this page.
             Only the delete is blocked. -->
        <p
          v-if="editing && editing.work_item_count"
          class="text-xs text-amber-900 bg-amber-50 border border-amber-300 rounded p-2 mb-3"
        >
          {{ editing.work_item_count }}
          work item{{ editing.work_item_count === 1 ? '' : 's' }}
          sit{{ editing.work_item_count === 1 ? 's' : '' }} in this sprint.
          A rename shows on every one of them, and new dates move the week
          they are planned for.
        </p>

        <p v-if="formError" class="text-sm text-red-700 mb-2 whitespace-pre-line">
          {{ formError }}
        </p>

        <div class="flex justify-end gap-2">
          <button
            class="text-sm px-3 py-1.5 border border-gray-400 rounded hover:bg-gray-100"
            @click="closeForm"
          >Cancel</button>
          <button
            class="text-sm px-3 py-1.5 rounded bg-accent-primary text-white
                   hover:bg-accent-secondary disabled:opacity-50"
            :disabled="saving"
            @click="save"
          >{{ saving ? 'Saving…' : 'Save' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { name: 'sprint-admin' }
</script>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useStore } from 'vuex'

import { InformationCircleIcon } from '@heroicons/vue/20/solid'

import {
  createSprint,
  deleteSprint,
  getSprints,
  updateSprint,
} from '@/services/BackOffice'

// Written once here rather than spelled out at each of the two places the
// table draws them -- the mobile cards and the desktop row carry the same
// three badges.
const BADGE = {
  current: 'text-xs px-1.5 py-0.5 rounded bg-accent-primary text-white',
  undated: 'text-xs px-1.5 py-0.5 rounded bg-gray-200 text-gray-600',
  overlap: 'text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 cursor-help',
}

const store = useStore()
const token = () => store.getters['auth/showToken']

// The route admits dev_group alone, so this is belt and braces -- but it is
// what decides whether the buttons are drawn at all, and a page that offers
// an Edit button the server will refuse is worse than one that does not.
const canEdit = computed(
  () => Boolean((store.getters['auth/showUserPermissions'] || {}).isDev),
)

const sprints = ref([])
const loading = ref(true)
const error = ref('')
const deleting = ref(null)

const search = ref('')
const year = ref('')
const emptyOnly = ref(false)

const formOpen = ref(false)
const editing = ref(null)
const saving = ref(false)
const formError = ref('')
// Whether the person has written these themselves. Until they have, the
// name and the end date follow the start date; once they have, they are
// theirs and nothing rewrites them underneath.
const nameTyped = ref(false)
const endTyped = ref(false)
const form = reactive({ name: '', start_date: '', end_date: '' })

async function load() {
  loading.value = true
  error.value = ''
  try {
    const data = await getSprints(token())
    sprints.value = Array.isArray(data) ? data : (data.results || [])
  } catch (e) {
    error.value = describe(e)
  } finally {
    loading.value = false
  }
}

onMounted(load)

// --- reading dates ------------------------------------------------------
//
// Everything here works on the 'YYYY-MM-DD' strings the API sends, split
// rather than passed to Date(): new Date('2026-11-09') is parsed as UTC
// midnight, which in Central is the evening before, so every date would
// read a day early and every weekday would be wrong.

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function parts(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '')
  return match ? [Number(match[1]), Number(match[2]), Number(match[3])] : null
}

// Noon UTC rather than midnight, so that nothing a browser does with
// daylight saving can push the day either side of the boundary.
function asDate(value) {
  const ymd = parts(value)
  return ymd ? new Date(Date.UTC(ymd[0], ymd[1] - 1, ymd[2], 12)) : null
}

function asString(date) {
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const day = String(date.getUTCDate()).padStart(2, '0')
  return `${date.getUTCFullYear()}-${month}-${day}`
}

function addDays(value, count) {
  const date = asDate(value)
  if (!date) return ''
  date.setUTCDate(date.getUTCDate() + count)
  return asString(date)
}

// "Mon 11/09/2026". The weekday is worth the width on this table: sprints
// here run Monday to Friday, so a start date that is not a Monday is the
// most likely mistake on the page and this is where it shows.
function dayLabel(value) {
  const ymd = parts(value)
  if (!ymd) return '—'
  const weekday = WEEKDAYS[asDate(value).getUTCDay()]
  return `${weekday} ${String(ymd[1]).padStart(2, '0')}/`
    + `${String(ymd[2]).padStart(2, '0')}/${ymd[0]}`
}

function rangeLabel(sprint) {
  if (!sprint.start_date) return 'No dates'
  return `${dayLabel(sprint.start_date)} → ${dayLabel(sprint.end_date)}`
}

// Inclusive, so a Monday-to-Friday week reads 5 rather than 4.
function dayCount(sprint) {
  const start = asDate(sprint.start_date)
  const end = asDate(sprint.end_date)
  if (!start || !end) return '—'
  return Math.round((end - start) / 86400000) + 1
}

function todayString() {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${now.getFullYear()}-${month}-${day}`
}

// The same rule the backlog board uses, so the row marked current here is
// the sprint the board is showing: the week containing today, or if today
// falls in a gap between sprints, the next one to start.
const currentId = computed(() => {
  const dated = sprints.value
    .filter(sprint => sprint.start_date && sprint.end_date)
    .sort((a, b) => (a.start_date < b.start_date ? -1 : 1))
  const today = todayString()
  const containing = dated.find(
    sprint => sprint.start_date <= today && today <= sprint.end_date,
  )
  if (containing) return containing.id
  const upcoming = dated.find(sprint => sprint.start_date > today)
  return upcoming ? upcoming.id : null
})

// Which sprints cover a day another sprint also covers, and what they
// collide with. There are none in the table today, and one created by
// accident would quietly change which week the board calls current -- so it
// is worth a badge rather than a rule: fixing an overlap may mean editing
// the other sprint, and a save that refuses leaves you unable to do either.
const overlaps = computed(() => {
  const dated = sprints.value
    .filter(sprint => sprint.start_date && sprint.end_date)
    .sort((a, b) => (a.start_date < b.start_date ? -1 : 1))
  const found = {}
  for (let i = 0; i < dated.length; i += 1) {
    for (let j = i + 1; j < dated.length; j += 1) {
      // Sorted by start, so once one starts after this one ends, so does
      // every sprint after it.
      if (dated[j].start_date > dated[i].end_date) break
      found[dated[i].id] = dated[j].name
      found[dated[j].id] = dated[i].name
    }
  }
  return found
})

const years = computed(() => {
  const found = new Set()
  sprints.value.forEach(sprint => { if (sprint.year) found.add(sprint.year) })
  return [...found].sort((a, b) => b - a)
})

const undatedCount = computed(
  () => sprints.value.filter(sprint => !sprint.start_date).length,
)

// Filtered in the browser, deliberately: the list endpoint is unpaginated
// and the board already loads every row, so asking the server to narrow it
// would be a second request for rows that are already here.
const shown = computed(() => {
  const term = search.value.trim().toLowerCase()
  return sprints.value.filter(sprint => {
    if (term && !String(sprint.name).toLowerCase().includes(term)) return false
    if (year.value === 'undated' && sprint.start_date) return false
    if (year.value && year.value !== 'undated'
      && String(sprint.year) !== year.value) return false
    if (emptyOnly.value && sprint.work_item_count) return false
    return true
  })
})

// --- the form -----------------------------------------------------------

// '2026: 11/09 - 11/13', which is how all 234 existing rows are named. Kept
// identical to them on purpose: the name is what the board prints, and a
// column heading in a different format reads as a different kind of thing.
function nameFor(start, end) {
  const from = parts(start)
  const to = parts(end)
  if (!from || !to) return ''
  const pad = n => String(n).padStart(2, '0')
  return `${from[0]}: ${pad(from[1])}/${pad(from[2])} - ${pad(to[1])}/${pad(to[2])}`
}

function renameFromDates() {
  if (nameTyped.value) return
  const suggested = nameFor(form.start_date, form.end_date)
  if (suggested) form.name = suggested
}

function startChanged() {
  // Monday to Friday is 199 of the 234 sprints in the table, so the end
  // date follows the start unless somebody has set one themselves.
  if (form.start_date && !endTyped.value) {
    form.end_date = addDays(form.start_date, 4)
  }
  renameFromDates()
}

function endChanged() {
  endTyped.value = true
  renameFromDates()
}

function openNew() {
  editing.value = null
  nameTyped.value = false
  endTyped.value = false
  form.name = ''
  form.start_date = ''
  form.end_date = ''
  formError.value = ''
  formOpen.value = true
}

function openEdit(sprint) {
  editing.value = sprint
  // An existing name and an existing end date are somebody's, however they
  // were arrived at, so editing one field of a saved sprint never rewrites
  // the others underneath.
  nameTyped.value = true
  endTyped.value = Boolean(sprint.end_date)
  form.name = sprint.name
  form.start_date = sprint.start_date || ''
  form.end_date = sprint.end_date || ''
  formError.value = ''
  formOpen.value = true
}

function closeForm() {
  formOpen.value = false
}

async function save() {
  saving.value = true
  formError.value = ''
  const payload = {
    name: form.name.trim(),
    // Empty inputs are sent as null rather than '', which the date field
    // rejects as badly formed -- and both have to go together, which the
    // server also checks.
    start_date: form.start_date || null,
    end_date: form.end_date || null,
  }
  try {
    if (editing.value) {
      await updateSprint(token(), editing.value.id, payload)
    } else {
      await createSprint(token(), payload)
    }
    formOpen.value = false
    await load()
  } catch (e) {
    formError.value = describe(e)
  } finally {
    saving.value = false
  }
}

// --- deleting -----------------------------------------------------------

// Every one of the 234 sprints in the table has work items on it, so this is
// false for all of them today and the button is drawn disabled: the sprint
// you can delete is one created here by mistake. The server refuses the rest
// regardless -- WorkItem.sprint is PROTECT -- and this only saves the trip.
function deletable(sprint) {
  return !sprint.work_item_count
}

function deleteTitle(sprint) {
  if (deletable(sprint)) return `Delete ${sprint.name}`
  const count = sprint.work_item_count
  return `${count} work item${count === 1 ? '' : 's'} still in this sprint. `
    + 'Move them off before deleting it.'
}

async function remove(sprint) {
  if (!deletable(sprint)) return
  if (!window.confirm(`Delete the sprint "${sprint.name}"?`)) return
  deleting.value = sprint.id
  error.value = ''
  try {
    await deleteSprint(token(), sprint.id)
    await load()
  } catch (e) {
    error.value = describe(e)
  } finally {
    deleting.value = null
  }
}

// What went wrong, in the server's own words where it has any. DRF answers a
// failed validation with a map of field to reasons and the 409 on a
// protected delete with a sentence, so both are unpacked rather than printed
// as JSON at somebody.
function describe(e) {
  const data = e && e.response && e.response.data
  if (typeof data === 'string' && data) return data
  if (data && typeof data === 'object') {
    const lines = []
    Object.entries(data).forEach(([field, value]) => {
      const text = Array.isArray(value) ? value.join(' ') : String(value)
      lines.push(field === 'detail' || field === 'non_field_errors'
        ? text
        : `${field}: ${text}`)
    })
    if (lines.length) return lines.join('\n')
  }
  if (e && e.response && e.response.status === 403) {
    return 'Only the dev group may change the sprint calendar.'
  }
  return (e && e.message) || 'Something went wrong.'
}
</script>
